import { useMemo, useState } from "react";
import { LoaderFunction, useNavigate } from "react-router-dom";

import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Close from "atoms/icon/icons/close.svg";
import Info from "atoms/icon/icons/info.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { Modal } from "atoms/modal/Modal";
import ProgressBar from "atoms/progress-bar/ProgressBar";
import { loadIfFlag } from "services/Flags";

import { steps } from "../QuestionnaireSteps";
import { useQuestionnaire } from "../useQuestionnaire";

export const questionnaireLoader: LoaderFunction = loadIfFlag("repeatmd-glp")(
  async () => {
    return null;
  }
);

const GlpQuestionnaire = () => {
  const [showClosingModal, setShowClosingModal] = useState(false);
  const navigate = useNavigate();
  const {
    currentStep,
    subStep,
    stepValues,
    progress,
    handleStepData,
    handleNext,
    handlePrev,
    errors,
  } = useQuestionnaire(steps);

  const CurrentStepComponent = useMemo(() => {
    return !subStep.isSubStep ? steps[currentStep]?.component : null;
  }, [currentStep, subStep.isSubStep]);

  const CurrentSubStepComponent = useMemo(() => {
    return subStep.isSubStep
      ? steps[currentStep]?.subSteps[subStep.count]?.component
      : null;
  }, [currentStep, subStep]);

  const handleClose = () => {
    localStorage.removeItem("glpQuestionnaire");
    localStorage.removeItem("currentStep");
    localStorage.removeItem("subStep");
    localStorage.removeItem("isSubStep");
    localStorage.removeItem("progress");
    navigate("/");
  };

  const goPreviousStep = () => {
    if (currentStep === 0) {
      handleClose();
    } else {
      handlePrev();
    }
  };

  return (
    <div className="flex h-full flex-col">
      {showClosingModal && (
        <Modal
          open={showClosingModal}
          onClose={() => setShowClosingModal(false)}
        >
          <div className="flex flex-col gap-3 py-4">
            <div>
              <IconButton
                svg={Close}
                onClick={() => setShowClosingModal(false)}
                size="small"
                style="iconOnly"
              />
            </div>
            <div className="flex flex-col items-center gap-3 px-6">
              <Icon svg={Info} className="text-center" />
              <p className="text-center font-serif text-h3">
                Abandon intake form?
              </p>
              <p className="text-center font-serif text-body1 text-secondary">
                Completing this form is required to determine your eligibility
                for this program. Your progress may not be saved.
              </p>
              <Button fullWidth onClick={handleClose}>
                Abandon and exit
              </Button>
              <Button
                style="secondary"
                fullWidth
                onClick={() => setShowClosingModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex w-full items-center justify-between gap-3 border-b-2 border-gray-50 bg-white bg-opacity-95 pt-[env(safe-area-inset-top)]">
        <div className="flex items-center">
          <IconButton
            svg={ChevronLeft}
            onClick={goPreviousStep}
            size="small"
            style="iconOnly"
          />
          <p className="text-md text-bold1">Back</p>
        </div>
        {currentStep > 0 && (
          <IconButton
            svg={Close}
            onClick={() => setShowClosingModal(true)}
            size="small"
            style="iconOnly"
          />
        )}
      </div>
      <div className="flex flex-grow flex-col overflow-hidden">
        <div className="flex flex-col gap-2 px-4 py-6">
          <ProgressBar
            value={(progress.questionsAnswered / progress.totalQuestions) * 100}
          />
          <p className="text-right text-xs">
            {progress.questionsAnswered}/{progress.totalQuestions}
          </p>
        </div>
        <div className="flex-grow overflow-y-auto px-4 pb-6">
          {CurrentStepComponent && (
            <CurrentStepComponent
              errors={errors}
              data={stepValues}
              handleStepData={handleStepData}
              next={handleNext}
            />
          )}
          {CurrentSubStepComponent && (
            <CurrentSubStepComponent
              errors={errors}
              data={stepValues}
              handleStepData={handleStepData}
              next={handleNext}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GlpQuestionnaire;
